import * as Yup from 'yup';
import moment from 'moment-timezone';
import { DEFAULT_TIMEZONE, getMinDate90DaysAgo, getMaxDate90DaysFromNow } from '../../../../utils/timezone';
import {
    isValidPositiveAmountMax15Integer,
    isValidPositiveAmountMax15IntegerMax2Decimals,
    isValidPositiveAmountMax15IntegerMax3Decimals,
    additionalChargesOtherChargeTest,
} from './additionalChargeValidation.helpers';

export const ValidationSchemaForRequestFinancingSupplier = Yup.object().shape({
    vessel_imo: Yup.string()
        .required('IMO number is required')
        .test('is-7-characters', 'IMO number must be exactly 7 characters long', (value) => value && value.length === 7)
        .matches(/^\d{7}$/, 'IMO number must be exactly 7 digits'),
    port: Yup.array().min(1, 'Port is required').required('Port is required'),
    //next_port: Yup.array().min(1, 'Next Port is required').required('Next Port is required'),
    delivery_start_date: Yup.date()
        .required('Start Date is required')
        .test('min-date-90-days', 'Start Date cannot be more than 90 days in the past', function(value) {
            if (!value) return true;
            // Get the minimum allowed date (90 days ago at start of day)
            const minDate = getMinDate90DaysAgo();
            // Normalize the input date to start of day for comparison
            const inputDate = moment(value).tz(DEFAULT_TIMEZONE).startOf('day').toDate();
            // Compare only dates, not times - this allows any time on the 90th day
            return inputDate >= minDate;
        }),
    delivery_end_date: Yup.date()
        .required('End Date is required')
        .test('min-date-90-days', 'End Date cannot be more than 90 days in the past', function(value) {
            if (!value) return true;
            // Get the minimum allowed date (90 days ago at start of day)
            const minDate = getMinDate90DaysAgo();
            // Normalize the input date to start of day for comparison
            const inputDate = moment(value).tz(DEFAULT_TIMEZONE).startOf('day').toDate();
            // Compare only dates, not times - this allows any time on the 90th day
            return inputDate >= minDate;
        })
        .min(Yup.ref('delivery_start_date'), 'End Date must be later than or equal to Start Date'),
    payment_term: Yup.string().required('Payment term is required'),
    delivery_mode: Yup.string().required('Delivery mode is required'),
    buyer_supplier_name: Yup.array().min(1, 'Buyer is required').required('Buyer is required'),
    //buyer_supplier_name: Yup.string().required('Supplier name is required'),
    products: Yup.array().of(
        Yup.object().shape({
            type: Yup.string().required('Type required'),
            volume: Yup.number()
                .typeError('Value must be a number')
                .test('is-valid-payment-amount', 'Max 15 digits & 3 decimals positive value', isValidPositiveAmountMax15IntegerMax3Decimals)
                .test('min-volume', 'Volume must be greater than or equal to the Min Volume', function (value) {
                    const { min_volume } = this.parent;
                    if (min_volume !== undefined && min_volume !== null) {
                        return value >= min_volume;
                    }
                    return true;
                })
                .required('Numeric field is required'),
            price: Yup.number()
                .typeError('Value must be a number')
                .test('is-valid-payment-amount', 'Max 15 digits & 2 decimals positive value', isValidPositiveAmountMax15IntegerMax2Decimals)
                .required('Numeric field is required'),

            min_volume: Yup.number()
                .typeError('Value must be a number')
                .nullable()
                .test('min-volume-less-than-volume', 'Min Volume must be less than or equal to Volume', function (value) {
                    const { volume } = this.parent;
                    if (value !== null && value !== undefined && volume !== null && volume !== undefined) {
                        return value <= volume;
                    }
                    return true;
                }),
        })
    ),
    additional_charges: Yup.array().of(
        Yup.object()
            .shape({
                amount: Yup.number()
                    .typeError('Value must be a number')
                    .test('is-valid-payment-amount', 'Max 15 digits integer part, positive value', isValidPositiveAmountMax15Integer),
                rounding_off: Yup.number().typeError('Value must be a number').nullable(),
            })
            .test(
                'other-charge-validation',
                'Amount and other charge name are required for "other" charge type',
                additionalChargesOtherChargeTest
            )
    ),
    foc_doc: Yup.array().min(1, 'Please upload at least one file').required('File upload is required'),
    //remark_sup_inv: Yup.string().required('Comment is required'),
});

export const ValidationSchemaForRequestFinancingBuyer = Yup.object().shape({
    vessel_imo: Yup.string()
        .required('IMO number is required')
        .test('is-7-characters', 'IMO number must be exactly 7 characters long', (value) => value && value.length === 7)
        .matches(/^\d{7}$/, 'IMO number must be exactly 7 digits'),
    port: Yup.array().min(1, 'Port is required').required('Port is required'),
    //next_port: Yup.array().min(1, 'Next Port is required').required('Next Port is required'),
    delivery_start_date: Yup.date()
        .required('Start Date is required')
        .test('min-date-90-days', 'Start Date cannot be more than 90 days in the past', function(value) {
            if (!value) return true;
            // Get the minimum allowed date (90 days ago at start of day)
            const minDate = getMinDate90DaysAgo();
            
            // Normalize the input date to start of day for comparison
            const inputDate = moment(value).tz(DEFAULT_TIMEZONE).startOf('day').toDate();
            
            // Compare only dates, not times - this allows any time on the 90th day
            return inputDate >= minDate;
        })
        .test('max-date-90-days', 'Start Date cannot be more than 90 days from today', function(value) {
            if (!value) return true;
            const maxDate = getMaxDate90DaysFromNow();
            const inputDate = moment(value).tz(DEFAULT_TIMEZONE).toDate();
            return inputDate <= maxDate;
        }),
    delivery_end_date: Yup.date()
        .required('End Date is required')
        .test('min-date-90-days', 'End Date cannot be more than 90 days in the past', function(value) {
            if (!value) return true;
            // Get the minimum allowed date (90 days ago at start of day)
            const minDate = getMinDate90DaysAgo();
            
            // Normalize the input date to start of day for comparison
            const inputDate = moment(value).tz(DEFAULT_TIMEZONE).startOf('day').toDate();
            
            // Compare only dates, not times - this allows any time on the 90th day
            return inputDate >= minDate;
        })
        .test('max-date-90-days', 'End Date cannot be more than 90 days from today', function(value) {
            if (!value) return true;
            const maxDate = getMaxDate90DaysFromNow();
            const inputDate = moment(value).tz(DEFAULT_TIMEZONE).toDate();
            return inputDate <= maxDate;
        })
        .min(Yup.ref('delivery_start_date'), 'End Date must be later than or equal to Start Date'),
    payment_term: Yup.string().required('Payment term is required'),
    delivery_mode: Yup.string().required('Delivery mode is required'),
    buyer_supplier_name: Yup.string().required('Supplier name is required'),
    products: Yup.array().of(
        Yup.object().shape({
            type: Yup.string().required('Type required'),
            volume: Yup.number()
                .typeError('Value must be a number')
                .test('is-valid-payment-amount', 'Max 15 digits integer part, positive value', isValidPositiveAmountMax15Integer)
                .test('min-volume', 'Volume must be greater than or equal to the Min Volume', function (value) {
                    const { min_volume } = this.parent;
                    if (min_volume !== undefined && min_volume !== null) {
                        return value >= min_volume;
                    }
                    return true;
                })
                .required('Numeric field is required'),
            price: Yup.number()
                .typeError('Value must be a number')
                .test('is-valid-payment-amount', 'Max 15 digits integer part, positive value', isValidPositiveAmountMax15Integer)
                .required('Numeric field is required'),

            min_volume: Yup.number()
                .typeError('Value must be a number')
                .nullable()
                .test('min-volume-less-than-volume', 'Min Volume must be less than or equal to Volume', function (value) {
                    const { volume } = this.parent;
                    if (value !== null && value !== undefined && volume !== null && volume !== undefined) {
                        return value <= volume;
                    }
                    return true;
                }),
        })
    ),
    additional_charges: Yup.array().of(
            Yup.object()
                .shape({
                    amount: Yup.number()
                        .typeError('Value must be a number')
                        .test('is-valid-payment-amount', 'Max 15 digits integer part, positive value', isValidPositiveAmountMax15Integer),
                    rounding_off: Yup.number().typeError('Value must be a number').nullable(),
                })
            .test(
                'other-charge-validation',
                'Amount and other charge name are required for "other" charge type',
                additionalChargesOtherChargeTest
            )
    ),
    foc_doc: Yup.array().min(1, 'Please upload at least one file').required('File upload is required'),
    //remark_sup_inv: Yup.string().required('Comment is required'),
});
